/**
 * WHAT WENT OUT, AND THE ONE DOOR BACK IN (moved here by P39)
 *
 * Three panels that all answer the same half of a ticket — the outbound half
 * once a mail has actually left the building — moved verbatim out of
 * `TicketDetail.tsx`, which was 1'756 lines. Nothing about what they DO changed
 * in the move; what changed is §3 and §8 presentation the kit now owns:
 *
 * - every card title is a real `<h2>` (audit S2-12: eight cards, zero headings);
 * - the hand-rolled `bg-amber-600` button in the correction panel is the kit's
 *   primary, so its height, focus ring and disabled appearance come from one
 *   place;
 * - the tints are §2 tokens (`brand` / `warn` / `err`) rather than stock
 *   `green-*` / `amber-*` / `red-*`;
 * - the 11 px evidence line under a delivery proof is 12 px (§3's floor).
 *
 * The panels are here together because a reader meets them together: a
 * delivered ticket shows its proof, the customer's whole delivery history, and
 * the correction — the only way to reopen a request that has been answered.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { ConfirmModal } from './ui/ConfirmModal';
import { Textarea } from './ui/Field';
import { useToast } from './ui/Toast';
import { useCreateCorrection } from '../hooks/useTicketsApi';
import { useDeliveryHistory } from '../hooks/useReportsApi';
import { downloadCsv, type CsvColumn } from '../lib/csv';
import { isoDateTime } from '../lib/dates';
import type { DeliveryHistoryRow, DeliveryProof, Ticket } from '../types/domain';

/**
 * THE CORRECTION (P24, Roman's decision 6).
 *
 * A reply for this ticket has already left the building. Until P24 that was the
 * end of the line: both recomposition guards list `'Sent'`, so "Compose again"
 * answered 409 forever, and P22 had to REFUSE reopening the ticket because a
 * reopened ticket could do nothing. `DRA-2026-00045` sat in exactly that state
 * for a day with a different chemical's safety data sheet already delivered.
 *
 * What this button does is honest about what a correction is: a SECOND mail in
 * the same thread. It copies what actually went out — wording, recipients,
 * candidate list — into a new `Correction` draft, reopens the ticket, and hands
 * the person to Approvals, where the draft editor and the attachment picker are.
 * Nothing is sent: it goes through the ordinary approval like every other reply.
 *
 * The reason is required and goes to the LEDGER, never to the customer. The
 * apology is wording a person writes on the draft, not a string this screen
 * invents.
 *
 * **It is the one primary of its view** (P39, task 5), and it can be: this panel
 * only renders once a reply has been sent, and in that state the Reply card
 * renders nothing and the compose control is gone.
 */
export function CorrectionPanel({ ticket }: { ticket: Ticket }) {
    const { toast } = useToast();
    const correction = useCreateCorrection(ticket.ticketNumber);
    const [reason, setReason] = useState('');
    const [confirm, setConfirm] = useState(false);

    return (
        <div className="rounded-card border border-warn-edge bg-warn-50 p-4 text-sm">
            <h2 className="text-sm font-semibold text-warn-ink">A reply for this ticket has already been sent</h2>
            <p className="mt-1 text-xs text-warn-ink">
                If the wrong thing went out, the answer is a correction: a new mail in the same thread, carrying what was
                sent so you can change it. It is written and approved like any other reply — nothing goes out until
                somebody approves it in <Link to="/approvals" className="underline">Approvals</Link>.
            </p>

            <label className="mt-3 block text-xs text-warn-ink" htmlFor="correction-reason">
                What was wrong with the mail that went out?
            </label>
            <Textarea
                id="correction-reason"
                value={reason}
                onChange={e => setReason(e.target.value)}
                rows={2}
                placeholder="e.g. the attached data sheet was for a different product"
                className="mt-1"
            />
            <div className="mt-2 flex flex-wrap items-center gap-3">
                <Button
                    variant="primary"
                    size="sm"
                    disabled={reason.trim().length < 5 || correction.isPending}
                    disabledReason="Write one line about what was wrong first — it goes to the audit trail, not to the customer."
                    onClick={() => setConfirm(true)}
                >
                    {correction.isPending ? 'Starting…' : 'Send a correction…'}
                </Button>
                <span className="text-xs text-warn-ink">
                    Recorded in the audit trail with your name. The customer never sees this sentence.
                </span>
            </div>

            {confirm && (
                <ConfirmModal
                    title="Start a correction for this ticket?"
                    message={
                        'A new draft is created as a copy of the mail that was sent — same thread, same recipients, same '
                        + 'document candidates. The ticket is reopened and the draft waits in Approvals, where you edit the '
                        + 'wording and choose the right document. Nothing is sent by this action. Your name and your reason '
                        + 'are recorded against it.'
                    }
                    confirmLabel="Yes - start the correction"
                    cancelLabel="Cancel"
                    variant="danger"
                    onConfirm={() => {
                        setConfirm(false);
                        correction.mutate({ reason: reason.trim() }, {
                            onSuccess: (result) => {
                                toast(
                                    `Correction draft ${result.draftId} created — edit it in Approvals and approve it there.`
                                    + (result.ticketReopened ? ' The ticket was reopened.' : ''),
                                    'success',
                                    { sticky: true },
                                );
                                setReason('');
                            },
                            onError: (err: unknown) => toast(
                                err instanceof Error ? err.message : 'Could not start the correction', 'error',
                            ),
                        });
                    }}
                    onCancel={() => setConfirm(false)}
                />
            )}
        </div>
    );
}

/**
 * Delivery proof — which revision, in which language, to whom, when.
 *
 * Katia asked for it as a complaint-handling aid (H38); Marcello asked for the
 * same facts on 2026-08-26 as a compliance obligation ("in order to provide
 * evidence in the event of complaints or disputes"). Every value here has been
 * in the database since P8.
 *
 * Shown whenever a mail was actually sent for the ticket, NOT only when the
 * ticket is Closed — `DRA-2026-00014` was sent under a C9 override and is still
 * `NeedsReview`, and hiding the evidence for exactly that case would defeat the
 * purpose.
 */
export function DeliveryProofPanel({ proof }: { proof: DeliveryProof }) {
    return (
        <div className="rounded-card border border-brand-100 bg-brand-50 p-4 text-sm">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-sm font-semibold text-brand-700">
                    Delivered {proof.sentAt ? <span className="tabular-nums">on {isoDateTime(proof.sentAt)}</span> : ''}
                </h2>
                <div className="text-xs text-muted">draft {proof.draftId} · {proof.draftType}</div>
            </div>

            <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-xs text-body sm:grid-cols-2">
                <div><dt className="inline text-muted">To: </dt><dd className="inline">{proof.toAddresses.join(', ') || '—'}</dd></div>
                {proof.ccAddresses.length > 0 && (
                    <div><dt className="inline text-muted">Cc: </dt><dd className="inline">{proof.ccAddresses.join(', ')}</dd></div>
                )}
                <div><dt className="inline text-muted">Approved by: </dt><dd className="inline">{proof.approvedBy ?? '—'}</dd></div>
                <div>
                    <dt className="inline text-muted">Text: </dt>
                    <dd className="inline">{proof.editedBeforeSend ? 'edited by a person before sending' : 'as composed from the template'}</dd>
                </div>
                <div className="sm:col-span-2"><dt className="inline text-muted">Subject: </dt><dd className="inline">{proof.subject}</dd></div>
            </dl>

            {proof.documents.length === 0 ? (
                <div className="mt-3 text-xs text-muted">This reply carried no document.</div>
            ) : (
                <ul className="mt-3 space-y-2">
                    {proof.documents.map(doc => (
                        <li key={doc.fileName} className="rounded-ctl border border-brand-100 bg-surface p-2">
                            <div className="text-sm font-medium text-ink">
                                {doc.webUrl
                                    ? <a href={doc.webUrl} target="_blank" rel="noopener noreferrer" className="text-brand-700 hover:underline">{doc.fileName}</a>
                                    : doc.fileName}
                            </div>
                            <div className="mt-0.5 text-xs text-muted">
                                Revision{' '}
                                {doc.revisionDate
                                    ? <span className="font-medium tabular-nums text-ink">{doc.revisionDate}</span>
                                    : <span className="font-medium text-warn-ink">not stated in the file name</span>}
                                {' · language '}
                                {doc.language
                                    ? <span className="font-medium text-ink">{doc.language}</span>
                                    : <span className="font-medium text-warn-ink">not stated in the file name</span>}
                            </div>
                            {doc.languageMismatchAcceptedBy && (
                                <div className="mt-1.5 rounded-ctl border border-err-edge bg-err-50 px-2 py-1 text-xs text-err">
                                    <strong>Sent under a C9 language override.</strong> The customer asked for{' '}
                                    <strong>{doc.requestedLanguage ?? 'another language'}</strong>; {doc.languageMismatchAcceptedBy} decided
                                    to deliver this version anyway.
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            {/* The evidence half: these two ids are what a dispute is actually
                answered with — the mail's own id in the recipient's world, and the
                correlation id that ties the whole run together in the ledger.
                P39: 11 px → 12 px, which is §3's floor and, on the one line a
                dispute is settled from, not a place to save 1 px. */}
            <div className="mt-3 border-t border-brand-100 pt-2 text-xs text-muted">
                <div className="break-all">Message-ID: <span className="font-mono">{proof.internetMessageId ?? '—'}</span></div>
                {proof.correlationId && (
                    <div className="break-all">
                        Correlation:{' '}
                        <Link to={`/audit-trail?correlationId=${encodeURIComponent(proof.correlationId)}`} className="font-mono text-brand-700 hover:underline">
                            {proof.correlationId}
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}

const DELIVERY_HISTORY_CSV_COLUMNS: CsvColumn<DeliveryHistoryRow>[] = [
    { header: 'Ticket', value: r => r.ticketNumber },
    { header: 'Document', value: r => r.hasDocument ? r.documentFileName : '(reply sent, no document)' },
    { header: 'Revision date', value: r => r.documentRevisionDate },
    { header: 'Language', value: r => r.documentLanguage },
    { header: 'Recipient', value: r => r.toAddresses.join('; ') },
    { header: 'Request received', value: r => r.requestReceivedAt },
    { header: 'Sent at', value: r => r.sentAt },
    { header: 'Internet Message ID', value: r => r.internetMessageId },
    { header: 'Correlation ID', value: r => r.correlationId },
];

/**
 * Every SDS ever sent to THIS customer, across every ticket (P19) —
 * generalises `DeliveryProofPanel` above from one ticket to the customer's
 * whole history, which is what a real dispute is framed around. Marcello §3:
 * "which revision and language were sent, to whom and when, in order to
 * provide evidence in the event of complaints or disputes."
 *
 * The customer is identified by the ERP surrogate key
 * (`CustomerId`+`CompanyCode`), never by name — DRA master data cannot
 * identify a customer any other way, so a ticket with neither resolved
 * renders nothing here rather than guessing.
 *
 * It stays in the WORK column although it is history, and that is the one place
 * §6's rule is bent: it is a seven-column table and the rail is 392 px. Stated
 * in the deviation log rather than solved by shrinking the type.
 */
export function CustomerHistoryPanel({ ticket }: { ticket: Ticket }) {
    const { data, isLoading } = useDeliveryHistory(ticket.customerId, ticket.companyCode);
    if (!ticket.customerId || !ticket.companyCode) return null;
    const rows = data ?? [];

    return (
        <Card
            title="Customer delivery history"
            /* The sentence explains a TABLE. Where there is no table it explained
               nothing and cost a third of the card's height, so the empty state
               says the one thing it has to say and stops. It still renders — "we
               looked and there is none" is the answer a dispute needs. */
            subtitle={rows.length > 0
                ? 'Every SDS ever sent to this customer, across every ticket — when it was asked for and when it went out. Evidence for a complaint or dispute.'
                : undefined}
            action={rows.length > 0 ? (
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => downloadCsv(`delivery-history-${ticket.customerId}.csv`, rows, DELIVERY_HISTORY_CSV_COLUMNS)}
                >
                    Download CSV
                </Button>
            ) : undefined}
        >
            {isLoading && <div className="text-xs text-muted">Loading…</div>}
            {!isLoading && rows.length === 0 && (
                <div className="text-xs text-muted">No delivery on record for this customer.</div>
            )}

            {!isLoading && rows.length > 0 && (
                <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                        <thead>
                            <tr className="text-left text-muted">
                                <th className="pb-1 pr-3 font-medium">Ticket</th>
                                <th className="pb-1 pr-3 font-medium">Document</th>
                                <th className="pb-1 pr-3 font-medium">Revision</th>
                                <th className="pb-1 pr-3 font-medium">Language</th>
                                <th className="pb-1 pr-3 font-medium">To</th>
                                <th className="pb-1 pr-3 font-medium">Requested</th>
                                <th className="pb-1 pr-3 font-medium">Sent</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={`${r.draftId}-${i}`} className="border-t border-hair-2">
                                    <td className="py-1 pr-3">
                                        {r.ticketNumber === ticket.ticketNumber
                                            ? r.ticketNumber
                                            : <Link to={`/tickets/${r.ticketNumber}`} className="text-info hover:underline">{r.ticketNumber}</Link>}
                                    </td>
                                    <td className="py-1 pr-3">
                                        {r.hasDocument ? r.documentFileName : <span className="text-muted">reply sent, no document</span>}
                                    </td>
                                    <td className="py-1 pr-3 tabular-nums">{r.documentRevisionDate ?? '—'}</td>
                                    <td className="py-1 pr-3">{r.documentLanguage ?? '—'}</td>
                                    <td className="py-1 pr-3">{r.toAddresses.join(', ') || '—'}</td>
                                    <td className="py-1 pr-3 tabular-nums">{isoDateTime(r.requestReceivedAt)}</td>
                                    <td className="py-1 pr-3 tabular-nums">{isoDateTime(r.sentAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    );
}
